import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';

import { RowActionsMenu } from './RowActionsMenu';
import { renderServiceCell } from './serviceCellRenderer';
import { ColumnDef } from './ServiceTable.types';

export interface ServiceTableRowProps {
  row: Record<string, unknown>;
  columns: ColumnDef[];
  selected: boolean;
  even: boolean;
  /** Render Location Name / Primary Allocation as plain text when false.
   *  Lets restricted users (clients, partners) see the value without a
   *  hyperlink that would 404 or hit a permission error. */
  canViewStructure: boolean;
  /** When true, omit the leading checkbox cell + trailing actions cell
   *  (eye preview + 3-dot menu) and disable selection toggling on row
   *  click. Used for client/customer visibility-rule users. */
  isClientCustomer?: boolean;
  onClick: () => void;
  onShiftClick: () => void;
  onPreview: () => void;
  onAction: (action: string) => void;
}

const CELL_SX = {
  fontSize: '11px',
  py: 0.25,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

export const ServiceTableRow: React.FC<ServiceTableRowProps> = React.memo(
  ({
    row,
    columns,
    selected,
    even,
    canViewStructure,
    isClientCustomer = false,
    onClick,
    onShiftClick,
    onPreview,
    onAction,
  }) => {
    const theme = useTheme();
    const serviceId = row.serviceId as number | undefined;

    const handleRowClick = (e: React.MouseEvent) => {
      if (isClientCustomer) return;
      if (e.shiftKey) {
        onShiftClick();
      } else {
        onClick();
      }
    };

    const getRowBg = () => {
      if (selected) return theme.palette.v4.tableSelected;
      if (even) return theme.palette.v4.tableEven;
      return 'background.paper';
    };

    const getHoverBg = () => {
      if (selected) return theme.palette.v4.tableSelectedHover;
      return theme.palette.v4.tableHover;
    };

    return (
      <TableRow
        data-testid="service-table-row"
        onClick={handleRowClick}
        sx={{
          bgcolor: getRowBg(),
          cursor: isClientCustomer ? 'default' : 'pointer',
          '&:hover': { bgcolor: getHoverBg() },
          transition: 'background-color 0.15s ease',
        }}
      >
        {!isClientCustomer && (
          <TableCell padding="checkbox" sx={{ ...CELL_SX, width: 48, minWidth: 48, maxWidth: 48 }}>
            <Checkbox
              size="small"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onClick()}
              slotProps={{ input: { 'aria-label': `Select service ${serviceId ?? ''}` } }}
            />
          </TableCell>
        )}
        {columns.map((col) => (
          <TableCell
            key={col.key}
            sx={{
              ...CELL_SX,
              ...(col.width !== undefined
                ? {
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                : {
                    ...(col.minWidth !== undefined ? { minWidth: col.minWidth } : {}),
                    ...(col.maxWidth !== undefined
                      ? { maxWidth: col.maxWidth, overflow: 'hidden' }
                      : {}),
                  }),
            }}
          >
            {renderServiceCell(col, row, theme, canViewStructure)}
          </TableCell>
        ))}
        {!isClientCustomer && (
          <TableCell
            sx={{
              ...CELL_SX,
              width: 70,
              position: 'sticky',
              right: 0,
              bgcolor: 'inherit',
              zIndex: 1,
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview();
                }}
                aria-label={`Preview service ${serviceId ?? ''}`}
                sx={{ color: 'v4.accent' }}
              >
                <VisibilityIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <RowActionsMenu serviceId={serviceId ?? 0} onAction={onAction} />
            </Box>
          </TableCell>
        )}
      </TableRow>
    );
  }
);

ServiceTableRow.displayName = 'ServiceTableRow';
